import { useCallback, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { FileText, Loader2, Trash2, Upload } from 'lucide-react';

import { api } from '../../../../convex/_generated/api';
import type { Id } from '../../../../convex/_generated/dataModel';
import { QuestionStep } from '../QuestionStep';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/use-toast';
import { friendlyAuthError } from '@/lib/auth-errors';
import { cn } from '@/lib/utils';
import { STEPS, type QuestionnaireAnswers } from '../questions';

interface BrandStepProps {
  answers: QuestionnaireAnswers;
  onChange: (next: Partial<QuestionnaireAnswers>) => void;
  projectId: Id<'projects'>;
}

const MAX = 600;
const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_EXT_TO_MIME: Record<string, string> = {
  pdf: 'application/pdf',
  md: 'text/markdown',
  markdown: 'text/markdown',
  txt: 'text/plain',
};
const ACCEPT_ATTR = '.pdf,.md,.markdown,.txt,application/pdf,text/markdown,text/plain';

type UploadState =
  | { kind: 'idle' }
  | { kind: 'uploading' }
  | { kind: 'finalizing' }
  | { kind: 'error'; message: string };

export function BrandStep({ answers, onChange, projectId }: BrandStepProps) {
  const step = STEPS.find((s) => s.id === 'brand')!;
  const value = answers.brand_info ?? '';
  const remaining = MAX - value.length;

  const { token } = useAuth();
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  const issueUploadUrl = useMutation(api.brandDocuments.issueUploadUrl);
  const finalize = useMutation(api.brandDocuments.finalize);
  const removeDoc = useMutation(api.brandDocuments.remove);

  const document = useQuery(
    api.brandDocuments.getForProject,
    token ? { token, projectId } : 'skip',
  );

  const [uploadState, setUploadState] = useState<UploadState>({ kind: 'idle' });
  const [isDragging, setIsDragging] = useState(false);

  const validateFile = (file: File): { ok: true; mime: string } | { ok: false; reason: string } => {
    if (file.size <= 0) return { ok: false, reason: 'The file is empty.' };
    if (file.size > MAX_BYTES) return { ok: false, reason: 'The file exceeds the 10 MB limit.' };
    // Browsers don't always report a MIME for .md / .txt; fall back to extension.
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    const mimeFromExt = ALLOWED_EXT_TO_MIME[ext];
    if (mimeFromExt) return { ok: true, mime: mimeFromExt };
    if (file.type === 'application/pdf') return { ok: true, mime: 'application/pdf' };
    if (file.type === 'text/markdown' || file.type === 'text/x-markdown') {
      return { ok: true, mime: 'text/markdown' };
    }
    if (file.type === 'text/plain') return { ok: true, mime: 'text/plain' };
    return { ok: false, reason: 'Only PDF, Markdown, or plain-text files are accepted.' };
  };

  const upload = useCallback(
    async (file: File) => {
      if (!token) return;
      const check = validateFile(file);
      if (!check.ok) {
        toast({ variant: 'destructive', title: 'File rejected', description: check.reason });
        return;
      }
      try {
        setUploadState({ kind: 'uploading' });
        const uploadUrl = await issueUploadUrl({ token, projectId });
        const result = await fetch(uploadUrl, {
          method: 'POST',
          headers: { 'Content-Type': check.mime },
          body: file,
        });
        if (!result.ok) {
          const detail = await result.text().catch(() => '');
          throw new Error(`Upload failed (${result.status}): ${detail || result.statusText}`);
        }
        const json = (await result.json()) as { storageId: string };
        if (!json.storageId) throw new Error('Convex did not return a storageId');

        setUploadState({ kind: 'finalizing' });
        await finalize({
          token,
          projectId,
          storageId: json.storageId as Id<'_storage'>,
          filename: file.name,
          contentType: check.mime,
          bytes: file.size,
        });
        setUploadState({ kind: 'idle' });
        toast({
          title: 'Document uploaded',
          description: 'AURA will read it during code generation.',
        });
      } catch (err) {
        setUploadState({ kind: 'error', message: friendlyAuthError(err) });
      }
    },
    [token, projectId, issueUploadUrl, finalize, toast],
  );

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    void upload(files[0]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    void upload(files[0]);
  };

  const onRemove = async () => {
    if (!token || !document) return;
    try {
      await removeDoc({ token, documentId: document._id });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not remove the document',
        description: friendlyAuthError(err),
      });
    }
  };

  const isBusy = uploadState.kind === 'uploading' || uploadState.kind === 'finalizing';
  const hasDoc = document !== null && document !== undefined;

  return (
    <QuestionStep step={step}>
      <div className="grid max-w-2xl gap-8">
        {/* Short brand description */}
        <div className="grid gap-2">
          <Label htmlFor="brand-info">
            Brand description <span className="text-graphite text-xs">(optional)</span>
          </Label>
          <Textarea
            id="brand-info"
            value={value}
            onChange={(e) => onChange({ brand_info: e.target.value.slice(0, MAX) })}
            placeholder="A short paragraph about who you are, what you do, and why it matters. AURA uses this as voice and as input to the hero copy."
            rows={6}
            maxLength={MAX}
          />
          <p className="font-mono text-[11px] text-graphite text-right tabular-nums">
            {remaining} characters left
          </p>
        </div>

        {/* Brand reference document */}
        <div className="grid gap-2">
          <div className="flex items-baseline justify-between">
            <Label>
              Brand reference document{' '}
              <span className="text-graphite text-xs">(optional, single file)</span>
            </Label>
            <span className="font-mono text-[11px] text-graphite">
              PDF · Markdown · Plain text · up to 10 MB
            </span>
          </div>
          <p className="text-xs text-graphite">
            Upload a resume, brand guide, or one-pager. AURA reads its text and uses it as
            authoritative content when generating your site — names, roles, dates, projects, and
            key phrasing land in the React code itself.
          </p>

          {hasDoc ? (
            <DocumentRow
              filename={document.filename}
              bytes={document.bytes}
              hasExtractedText={document.hasExtractedText}
              extractedTextLength={document.extractedTextLength}
              extractionError={document.extractionError}
              extractedAt={document.extractedAt}
              onRemove={onRemove}
            />
          ) : (
            <div
              onDragOver={(e) => {
                if (isBusy || !token) return;
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={onDrop}
              className={cn(
                'relative flex flex-col items-center justify-center gap-3 rounded-sm border-2 border-dashed bg-paper-2/40 p-8 text-center transition-colors',
                isBusy || !token
                  ? 'cursor-not-allowed opacity-60 border-ink/20'
                  : isDragging
                    ? 'border-ochre bg-ochre/5'
                    : 'border-ink/25 hover:border-ink/40',
              )}
            >
              {isBusy ? (
                <Loader2 className="h-6 w-6 animate-spin text-ochre" aria-hidden />
              ) : (
                <Upload className="h-6 w-6 text-graphite" aria-hidden />
              )}
              <div>
                <p className="font-serif text-base italic text-ink">
                  {isBusy
                    ? uploadState.kind === 'uploading'
                      ? 'Uploading…'
                      : 'Finalising…'
                    : 'Drop a document here.'}
                </p>
                <p className="mt-1 text-sm text-graphite">
                  or{' '}
                  <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={isBusy || !token}
                    className="underline decoration-ink/30 underline-offset-4 hover:decoration-ochre hover:text-ochre disabled:no-underline disabled:cursor-not-allowed"
                  >
                    browse your files
                  </button>
                </p>
              </div>
              <input
                ref={inputRef}
                type="file"
                aria-label="Upload brand reference document"
                accept={ACCEPT_ATTR}
                onChange={onPick}
                className="hidden"
                disabled={isBusy || !token}
              />
            </div>
          )}

          {uploadState.kind === 'error' ? (
            <div className="rounded-sm border border-vermilion/50 bg-vermilion/5 px-3 py-2 text-sm text-vermilion">
              {uploadState.message}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setUploadState({ kind: 'idle' })}
                className="ml-3 h-6 px-2 font-mono text-[11px]"
              >
                Dismiss
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </QuestionStep>
  );
}

interface DocumentRowProps {
  filename: string;
  bytes: number;
  hasExtractedText: boolean;
  extractedTextLength: number;
  extractionError: string | null;
  extractedAt: number | null;
  onRemove: () => void;
}

function DocumentRow({
  filename,
  bytes,
  hasExtractedText,
  extractedTextLength,
  extractionError,
  extractedAt,
  onRemove,
}: DocumentRowProps) {
  const status = extractionError
    ? { label: 'Could not read', className: 'text-vermilion' }
    : hasExtractedText
      ? {
          label: `Read ${extractedTextLength.toLocaleString()} chars`,
          className: 'text-emerald-800',
        }
      : extractedAt === null
        ? { label: 'Will be read at generation time', className: 'text-graphite' }
        : { label: 'Pending extraction…', className: 'text-graphite' };

  return (
    <div className="flex items-start gap-3 rounded-sm border border-ink/15 bg-paper-2/50 p-3">
      <FileText className="mt-0.5 h-5 w-5 text-ochre" aria-hidden />
      <div className="flex-1 min-w-0">
        <p className="truncate font-mono text-sm text-ink">{filename}</p>
        <p className="font-mono text-[11px] text-graphite">
          {humanBytes(bytes)} ·{' '}
          <span className={status.className}>{status.label}</span>
        </p>
        {extractionError ? (
          <p className="mt-1 text-xs text-vermilion">{extractionError}</p>
        ) : null}
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={onRemove}
        className="h-7 px-2 font-mono text-[11px]"
        aria-label="Remove document"
      >
        <Trash2 className="h-3.5 w-3.5" />
        Remove
      </Button>
    </div>
  );
}

function humanBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}
